import { create } from 'zustand';
import {
  getUnreadCount,
  getWhatsAppStatus,
  type WhatsAppHistorySync,
  type WhatsAppProviderType,
} from '@/lib/whatsapp-api';
import { useAuthStore } from '@/stores/auth-store';
import type { WhatsAppAccountStatus, WhatsAppMessage } from '@/types';

/**
 * The status poll runs from the app shell on every page, for every signed-in
 * user — including ones with no WhatsApp grant at all, such as a lab
 * technician. Those calls could only ever 403, and the caught-and-logged
 * failure surfaced in the dev overlay as a bare "Request failed with status
 * code 403" on screens that have nothing to do with WhatsApp.
 *
 * Checked here rather than at the call site because the shell is not the only
 * caller: the socket provider re-fetches on reconnect too.
 */
function mayReadWhatsApp(): boolean {
  return useAuthStore.getState().user?.permissions?.includes('whatsapp.view') ?? false;
}

interface AccountStatusInfo {
  status: WhatsAppAccountStatus;
  phoneNumber?: string;
  name: string;
  providerType?: WhatsAppProviderType;
  /**
   * History-import progress, refreshed by the same 60s shell poll that carries
   * the connection status. Undefined on backends that predate the import.
   */
  historySync?: WhatsAppHistorySync;
}

interface WhatsAppState {
  // State
  unreadCount: number;
  accountStatuses: Record<string, AccountStatusInfo>;
  isConnected: boolean;
  lastFetched: number | null;

  // Actions
  fetchStatus: () => Promise<void>;
  setUnreadCount: (count: number) => void;
  incrementUnread: () => void;
  decrementUnread: (amount?: number) => void;
  updateAccountStatus: (accountId: string, status: WhatsAppAccountStatus, phoneNumber?: string, name?: string) => void;
  setConnected: (connected: boolean) => void;

  // Socket event handlers
  onMessageReceived: (conversationId: string, message: WhatsAppMessage) => void;
  onAccountStatusChanged: (accountId: string, status: WhatsAppAccountStatus, phoneNumber?: string) => void;
}

export const useWhatsAppStore = create<WhatsAppState>((set, get) => ({
  unreadCount: 0,
  accountStatuses: {},
  isConnected: false,
  lastFetched: null,

  fetchStatus: async () => {
    if (!mayReadWhatsApp()) {
      return;
    }

    try {
      const [statusResponse, unreadResponse] = await Promise.all([
        getWhatsAppStatus(),
        getUnreadCount(),
      ]);

      const statuses: Record<string, AccountStatusInfo> = {};
      statusResponse.accounts.forEach((account) => {
        statuses[account.id] = {
          status: account.status,
          phoneNumber: account.phone_number || undefined,
          name: account.name,
          providerType: account.provider_type,
          historySync: account.history_sync,
        };
      });

      set({
        accountStatuses: statuses,
        unreadCount: unreadResponse.count,
        lastFetched: Date.now(),
      });
    } catch (error) {
      console.error('Failed to fetch WhatsApp status', error);
    }
  },

  setUnreadCount: (count: number) => {
    set({ unreadCount: count });
  },

  incrementUnread: () => {
    set((state) => ({ unreadCount: state.unreadCount + 1 }));
  },

  decrementUnread: (amount = 1) => {
    set((state) => ({
      unreadCount: Math.max(0, state.unreadCount - amount),
    }));
  },

  updateAccountStatus: (accountId: string, status: WhatsAppAccountStatus, phoneNumber?: string, name?: string) => {
    set((state) => {
      const existing = state.accountStatuses[accountId];
      return {
        accountStatuses: {
          ...state.accountStatuses,
          [accountId]: {
            ...existing,
            status,
            phoneNumber: phoneNumber ?? existing?.phoneNumber,
            name: name ?? existing?.name ?? '',
          },
        },
      };
    });
  },

  setConnected: (connected: boolean) => {
    set({ isConnected: connected });
  },

  // Called when socket receives a new message
  onMessageReceived: (_conversationId: string, message: WhatsAppMessage) => {
    // Only a message from the other side is "new". A reply the agent typed
    // on the phone also arrives here (direction outgoing) and must not count.
    if (message.direction === 'incoming') {
      get().incrementUnread();
    }
  },

  // Called when socket receives account status change
  onAccountStatusChanged: (accountId: string, status: WhatsAppAccountStatus, phoneNumber?: string) => {
    get().updateAccountStatus(accountId, status, phoneNumber);
  },
}));
